import { Icon } from '@iconify/react';
import React, { useEffect, useState, useCallback } from 'react';
import { changePassword, editProfile, getUserProfileData } from '../utils/api/userAPI';
import { convertToIndianTimeZone } from '../utils/utils';
import { Avatar } from './child/Avatar';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import { useSelector } from 'react-redux';
import { useDispatch } from 'react-redux';
import { fetchCurrentUser } from '../store/app/userSlice';

const ViewProfileLayer = () => {
  const [imagePreview, setImagePreview] = useState(
    'assets/images/user-grid/user-grid-img13.png'
  );
  const dispatch = useDispatch();
  const userData = useSelector((state) => state.user.user);
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [newPasswordVisible, setNewPasswordVisible] = useState(false);

  const [editFormData, setEditFormData] = useState({
    firstName: '',
    lastName: '',
    phone: '',
  });

  const [changePasswordForm, setChangePassword] = useState({
    currentPassword: '',
    newPassword: '',
  });

  useEffect(() => {
    if (userData) {
      setEditFormData({
        firstName: userData.firstName || '',
        lastName: userData.lastName || '',
        phone: userData.phone || '',
      });
    }
  }, [userData]);

  const handleUpdatePassword = async () => {
    try {
      await changePassword(changePasswordForm);
      toast.success('Password changed!');
      setChangePassword({ currentPassword: '', newPassword: '' });
    } catch (e) {
      toast.error(e.message || 'Failed to change password');
    }
  };

  const handleInputChange = (e) => {
    const { id, value } = e.target;
    setEditFormData((prev) => ({ ...prev, [id]: value }));
  };

  const handlePasswordInputChange = (e) => {
    const { id, value } = e.target;
    setChangePassword((prev) => ({ ...prev, [id]: value }));
  };

  const handleCancel = () => {
    if (userData) {
      setEditFormData({
        firstName: userData.firstName || '',
        lastName: userData.lastName || '',
        phone: userData.phone || '',
      });
    } else {
      setEditFormData({ firstName: '', lastName: '', phone: '' });
    }
  };

  const handleSave = useCallback(async () => {
    try {
      await editProfile(editFormData);
      await dispatch(fetchCurrentUser()).unwrap();
      toast.success('Profile saved!');
    } catch (err) {
      console.error('Error saving profile:', err);
      toast.error(err.message || 'Failed to save profile');
    }
  }, [editFormData, dispatch]);

  const togglePasswordVisibility = () => setPasswordVisible((prev) => !prev);
  const toggleNewPasswordVisibility = () => setNewPasswordVisible((prev) => !prev);

  const readURL = (input) => {
    if (input.target.files && input.target.files[0]) {
      const reader = new FileReader();
      reader.onload = (e) => setImagePreview(e.target.result);
      reader.readAsDataURL(input.target.files[0]);
    }
  };

  if (!userData) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="loader">Loading...</div>
      </div>
    );
  }

  const user = userData;

  return (
    <motion.div initial={{
        opacity: 0
    }}
    animate={{
        opacity: 1
    }}
    transition={{
        duration: 1
    }}
    className="row gy-4">
      {/* ─── LEFT COLUMN: PROFILE CARD ─────────────────────────────────────── */}
      <div className="col-lg-4">
        <div className="user-grid-card position-relative border radius-16 overflow-hidden bg-base h-100">
          <div className="pb-24 ms-16 mb-24 me-16" style={{ marginTop: '20px' }}>
            <div className="text-center border-top-0 border-start-0 ">
              <Avatar fullName={user.displayName} />
            </div>
            <div className="text-center border border-top-0 border-start-0 border-end-0">
              <h6 className="mb-0 mt-16">{user.displayName}</h6>
              <span className="text-secondary-light mb-16">{user.email}</span>
            </div>

            <div className="mt-24">
              <h6 className="text-xl mb-16">Personal Info</h6>
              <ul>
                <li className="d-flex align-items-center gap-1 mb-12">
                  <span className="w-30 text-md fw-semibold text-primary-light">
                    Full Name
                  </span>
                  <span className="w-70 text-secondary-light fw-medium">
                    : {user.displayName}
                  </span>
                </li>

                <li className="d-flex align-items-center gap-1 mb-12">
                  <span className="w-30 text-md fw-semibold text-primary-light">
                    Email
                  </span>
                  <span className="w-70 text-secondary-light fw-medium">
                    : {user.email}
                  </span>
                </li>

                <li className="d-flex align-items-center gap-1 mb-12">
                  <span className="w-30 text-md fw-semibold text-primary-light">
                    Phone Number
                  </span>
                  <span className="w-70 text-secondary-light fw-medium">
                    : {user.phone}
                  </span>
                </li>

                <li className="d-flex align-items-center gap-1 mb-12">
                  <span className="w-30 text-md fw-semibold text-primary-light">
                    Organization Name
                  </span>
                  <span className="w-70 text-secondary-light fw-medium">
                    : {user.organizationName}
                  </span>
                </li>

                {user.preferredLanguage && user.preferredLanguage !== 'string' && (
                  <li className="d-flex align-items-center gap-1 mb-12">
                    <span className="w-30 text-md fw-semibold text-primary-light">
                      Languages
                    </span>
                    <span className="w-70 text-secondary-light fw-medium">
                      : {user.preferredLanguage}
                    </span>
                  </li>
                )}

                <li className="d-flex align-items-center gap-1">
                  <span className="w-30 text-md fw-semibold text-primary-light">
                    Updated At
                  </span>
                  <span className="w-70 text-secondary-light fw-medium">
                    : {convertToIndianTimeZone(user.updatedAt)}
                  </span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* ─── RIGHT COLUMN: TABS FOR EDIT / CHANGE PASSWORD ──────────────────── */}
      <div className="col-lg-8 ">
        <div className="card h-100 ">
          <div className="card-body p-24">
            <ul
              className="nav border-gradient-tab nav-pills mb-20 d-inline-flex"
              id="pills-tab"
              role="tablist"
            >
              <li className="nav-item" role="presentation">
                <button
                  className="nav-link d-flex align-items-center px-24 active"
                  id="pills-edit-profile-tab"
                  data-bs-toggle="pill"
                  data-bs-target="#pills-edit-profile"
                  type="button"
                  role="tab"
                  aria-controls="pills-edit-profile"
                  aria-selected="true"
                >
                  Edit Profile
                </button>
              </li>

              <li className="nav-item" role="presentation">
                <button
                  className="nav-link d-flex align-items-center px-24"
                  id="pills-change-password-tab"
                  data-bs-toggle="pill"
                  data-bs-target="#pills-change-password"
                  type="button"
                  role="tab"
                  aria-controls="pills-change-password"
                  aria-selected="false"
                  tabIndex={-1}
                >
                  Change Password
                </button>
              </li>
            </ul>

            <div className="tab-content " id="pills-tabContent">
              {/* ─── EDIT PROFILE TAB ────────────────────────────────────────────── */}
              <div
                className="tab-pane fade show active "
                id="pills-edit-profile"
                role="tabpanel"
                aria-labelledby="pills-edit-profile-tab"
                tabIndex={0}
              >
                <form>
                  <div className="row">
                    <div className="">
                      <div className="mb-20">
                        <label
                          htmlFor="firstName"
                          className="form-label fw-semibold text-primary-light text-sm mb-8"
                        >
                          First Name <span className="text-danger-600">*</span>
                        </label>
                        <input
                          type="text"
                          className="form-control radius-8"
                          id="firstName"
                          placeholder="Enter First Name"
                          value={editFormData.firstName}
                          onChange={handleInputChange}
                        />
                      </div>
                    </div>

                    <div className="">
                      <div className="mb-20">
                        <label
                          htmlFor="lastName"
                          className="form-label fw-semibold text-primary-light text-sm mb-8"
                        >
                          Last Name <span className="text-danger-600">*</span>
                        </label>
                        <input
                          type="text"
                          className="form-control radius-8"
                          id="lastName"
                          placeholder="Enter Last Name"
                          value={editFormData.lastName}
                          onChange={handleInputChange}
                        />
                      </div>
                    </div>

                    <div className="">
                      <div className="mb-20">
                        <label
                          htmlFor="phone"
                          className="form-label fw-semibold text-primary-light text-sm mb-8"
                        >
                          Phone <span className="text-danger-600">*</span>
                        </label>
                        <input
                          type="tel"
                          className="form-control radius-8"
                          id="phone"
                          placeholder="Enter phone number"
                          value={editFormData.phone}
                          onChange={handleInputChange}
                        />
                      </div>
                    </div>
                  </div>

                  <div className="d-flex align-items-center justify-content-center gap-3">
                    <button
                      type="button"
                      className="border border-danger-600 bg-hover-danger-200 text-danger-600 text-md px-56 py-11 radius-8"
                      onClick={handleCancel}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="btn btn-primary border border-primary-600 text-md px-56 py-12 radius-8"
                      style={{
                        backgroundColor: 'var(--primary-600)',
                      }}
                      onClick={handleSave}
                    >
                      Save
                    </button>
                  </div>
                </form>
              </div>

              {/* ─── CHANGE PASSWORD TAB ─────────────────────────────────────────── */}
              <div
                className="tab-pane fade"
                id="pills-change-password"
                role="tabpanel"
                aria-labelledby="pills-change-password-tab"
                tabIndex={0}
              >
                {/* ─ Current Password Field ─ */}
                <div className="mb-20">
                  <label
                    htmlFor="currentPassword"
                    className="form-label fw-semibold text-primary-light text-sm mb-8"
                  >
                    Current Password <span className="text-danger-600">*</span>
                  </label>
                  <div className="position-relative">
                    <input
                      type={passwordVisible ? 'text' : 'password'}
                      className="form-control radius-8"
                      id="currentPassword"
                      placeholder="Enter Current Password"
                      value={changePasswordForm.currentPassword}
                      onChange={handlePasswordInputChange}
                    />
                    <span
                      className="cursor-pointer position-absolute end-0 top-50 translate-middle-y me-16 text-secondary-light"
                      onClick={togglePasswordVisibility}
                    >
                      <Icon
                        icon={passwordVisible ? 'ri:eye-off-line' : 'ri:eye-line'}
                        fontSize="1.2rem"
                      />
                    </span>
                  </div>
                </div>

                {/* ─ New Password Field ─ */}
                <div className="mb-20">
                  <label
                    htmlFor="newPassword"
                    className="form-label fw-semibold text-primary-light text-sm mb-8"
                  >
                    New Password <span className="text-danger-600">*</span>
                  </label>
                  <div className="position-relative">
                    <input
                      type={newPasswordVisible ? 'text' : 'password'}
                      className="form-control radius-8"
                      id="newPassword"
                      placeholder="Enter New Password"
                      value={changePasswordForm.newPassword}
                      onChange={handlePasswordInputChange}
                    />
                    <span
                      className="cursor-pointer position-absolute end-0 top-50 translate-middle-y me-16 text-secondary-light"
                      onClick={toggleNewPasswordVisibility}
                    >
                      <Icon
                        icon={
                          newPasswordVisible ? 'ri:eye-off-line' : 'ri:eye-line'
                        }
                        fontSize="1.2rem"
                      />
                    </span>
                  </div>
                </div>

                {/* ─ Submit Change Password Button ─ */}
                <div className="d-flex align-items-center justify-content-center gap-3">
                  <button
                    type="button"
                    className="btn btn-primary border border-primary-600 text-md px-56 py-12 radius-8"
                    onClick={handleUpdatePassword}
                    style={{
                      backgroundColor: 'var(--primary-600)',
                    }}
                  >
                    Update Password
                  </button>
                </div>
              </div>
              {/* ─ End: Change Password Tab ──────────────────────────────────── */}
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default ViewProfileLayer;
